import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";

export type BudgetMap = Record<string, number>; // chart_of_accounts_id -> amount

interface UseBudgetsOptions {
  year: number;
  month: number;
}

/**
 * Hook para gerenciar orçamentos (budgets) por conta contábil + ano + mês.
 * Mantém um cache local em memória que é atualizado de forma otimista.
 */
export function useBudgets({ year, month }: UseBudgetsOptions) {
  const { user } = useAuth();
  const [budgets, setBudgets] = useState<BudgetMap>({});
  const [loading, setLoading] = useState(false);
  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const fetchBudgets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("budgets")
        .select("chart_of_accounts_id, amount")
        .eq("year", year)
        .eq("month", month);

      if (error) throw error;

      const map: BudgetMap = {};
      (data || []).forEach((row: any) => {
        map[row.chart_of_accounts_id] = Number(row.amount) || 0;
      });
      setBudgets(map);
    } catch (err: any) {
      toast({
        title: "Erro ao carregar orçamentos",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user, year, month]);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  /**
   * Atualiza otimisticamente o valor local e persiste no banco com debounce.
   */
  const setBudgetValue = useCallback(
    (chartOfAccountsId: string, amount: number) => {
      // Otimista
      setBudgets((prev) => ({ ...prev, [chartOfAccountsId]: amount }));

      // Debounce save
      const key = chartOfAccountsId;
      if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
      debounceRefs.current[key] = setTimeout(async () => {
        if (!user) return;
        try {
          const { error } = await supabase
            .from("budgets")
            .upsert(
              {
                user_id: user.id,
                chart_of_accounts_id: chartOfAccountsId,
                year,
                month,
                amount,
              },
              { onConflict: "organization_id,chart_of_accounts_id,year,month" }
            );
          if (error) throw error;
        } catch (err: any) {
          toast({
            title: "Erro ao salvar orçamento",
            description: err.message,
            variant: "destructive",
          });
          // refetch para recuperar estado real
          fetchBudgets();
        }
      }, 600);
    },
    [user, year, month, fetchBudgets]
  );

  /**
   * Persiste vários valores de uma vez (Copiar Realizado, Repetir mês anterior).
   */
  const bulkUpsert = useCallback(
    async (entries: { chartOfAccountsId: string; amount: number }[]) => {
      if (!user || entries.length === 0) return;
      try {
        const payload = entries.map((e) => ({
          user_id: user.id,
          chart_of_accounts_id: e.chartOfAccountsId,
          year,
          month,
          amount: e.amount,
        }));
        const { error } = await supabase
          .from("budgets")
          .upsert(payload, {
            onConflict: "organization_id,chart_of_accounts_id,year,month",
          });
        if (error) throw error;

        // Atualizar local
        setBudgets((prev) => {
          const next = { ...prev };
          entries.forEach((e) => {
            next[e.chartOfAccountsId] = e.amount;
          });
          return next;
        });
        toast({ title: "Orçamento atualizado", description: `${entries.length} contas salvas.` });
      } catch (err: any) {
        toast({
          title: "Erro ao salvar orçamentos",
          description: err.message,
          variant: "destructive",
        });
      }
    },
    [user, year, month]
  );

  /**
   * Busca orçamento de outro mês (usado para "Repetir mês anterior").
   */
  const fetchBudgetsForPeriod = useCallback(
    async (y: number, m: number): Promise<BudgetMap> => {
      if (!user) return {};
      const { data, error } = await supabase
        .from("budgets")
        .select("chart_of_accounts_id, amount")
        .eq("year", y)
        .eq("month", m);
      if (error) {
        toast({ title: "Erro ao buscar período", description: error.message, variant: "destructive" });
        return {};
      }
      const map: BudgetMap = {};
      (data || []).forEach((row: any) => {
        map[row.chart_of_accounts_id] = Number(row.amount) || 0;
      });
      return map;
    },
    [user]
  );

  return {
    budgets,
    loading,
    setBudgetValue,
    bulkUpsert,
    fetchBudgetsForPeriod,
    refetch: fetchBudgets,
  };
}
